import { getCountryDisplayName } from "./countries";

// Typed error codes returned by phone verification use-cases.
// Both backend and frontend reference these constants to avoid magic strings.
// Backend normalizes Twilio error codes to these; frontend maps them to
// user-friendly messages without fragile substring matching.
// Twilio error codes mapped to our internal constants.
// Reference: https://www.twilio.com/docs/api/errors
export const TwilioErrorCode = {
  VERIFICATION_NOT_FOUND: 20404,
  TOO_MANY_REQUESTS: 20429,
  INVALID_NUMBER: 60200,
  MAX_CHECK_ATTEMPTS: 60202,
  MAX_SEND_ATTEMPTS: 60203,
  // Verify sends on `channel: "sms"`, which no landline can receive. The
  // pre-send Lookup gate only refuses `nonFixedVoip`, so landlines reach the
  // send and fail here.
  SMS_NOT_SUPPORTED_BY_LANDLINE: 60205,
  // Per-number concurrency throttle, distinct from the account-wide 20429 and
  // from the 60202 attempt ceiling: nothing is consumed and the pending
  // verification stays valid, so the caller may retry the same code.
  MAX_CONCURRENT_REQUESTS: 60212,
  GEO_PERMISSION_BLOCKED: 60220,
  // Twilio's Fraud Guard temporarily blocking the number's prefix, not a
  // refusal of ours.
  DESTINATION_FRAUD_BLOCKED: 60410,
  // No carrier record for this number in Twilio's data, or it sits outside
  // their coverage. Distinct from a malformed number (60200): this one parses.
  // Twilio treats it as a data/coverage gap on their side, so it can clear
  // without the participant changing anything; do not cache it as permanent.
  UNPROVISIONED_OR_OUT_OF_COVERAGE: 60600,
  VERIFY_DELIVERY_BLOCKED: 60605,
} as const;

export const PhoneVerificationError = {
  INVALID_NUMBER: "PHONE_INVALID_NUMBER",
  RATE_LIMITED: "PHONE_RATE_LIMITED",
  SMS_DAILY_LIMIT: "PHONE_SMS_DAILY_LIMIT",
  SMS_COOLDOWN: "PHONE_SMS_COOLDOWN",
  ADDITIONAL_VERIFICATION_REQUIRED: "PHONE_ADDITIONAL_VERIFICATION_REQUIRED",
  SMS_UNAVAILABLE: "PHONE_SMS_UNAVAILABLE",
  VOIP_NOT_SUPPORTED: "PHONE_VOIP_NOT_SUPPORTED",
  SMS_GEO_BLOCKED: "PHONE_SMS_GEO_BLOCKED",
  DESTINATION_BLOCKED: "PHONE_DESTINATION_BLOCKED",
  // Twilio has no route to the number rather than a block on it, so the copy
  // must not imply anyone refused it.
  NUMBER_UNREACHABLE: "PHONE_NUMBER_UNREACHABLE",
  LANDLINE_NOT_SUPPORTED: "PHONE_LANDLINE_NOT_SUPPORTED",
  // The catch-all. Every branch above names a cause; this one exists so an
  // unmapped vendor failure still leaves through a constant rather than the
  // vendor's own prose, which reaches the client as an unmatched code and
  // degrades to the generic copy anyway. It intentionally has no branch in
  // `friendlyPhoneError`, so it resolves to that same generic copy; adding a
  // named cause is always better than widening this.
  SEND_FAILED: "PHONE_SEND_FAILED",
  // Carries no country. The blocked country travels beside the code (ApiError
  // metadata on the thrown path, `errorCountry` on the returned one) so one
  // defect stays one Error Tracking issue instead of one per country.
  COUNTRY_NOT_SUPPORTED: "PHONE_COUNTRY_NOT_SUPPORTED",
  // Another account that holds this number verified is banned. The copy says so
  // plainly, but never names which account: the person on this screen may have
  // inherited a recycled number rather than be the banned one.
  BANNED_ACCOUNT: "PHONE_BANNED_ACCOUNT",
} as const;

/**
 * Maps phone-verification error codes to user-friendly messages.
 * Backend normalizes all Twilio errors to PhoneVerificationError constants;
 * this function maps them to display strings.
 *
 * `country` is the ISO alpha-2 the backend reports alongside
 * COUNTRY_NOT_SUPPORTED; it is the only code whose copy names the country.
 */
export function friendlyPhoneError(
  msg: string,
  country?: string | null,
  retryAfterSeconds?: number,
): string {
  if (msg === PhoneVerificationError.INVALID_NUMBER) {
    return "This number doesn\u2019t seem right. Please go back and double-check it.";
  }
  if (msg === PhoneVerificationError.RATE_LIMITED) {
    return "Too many verification attempts. Please wait a few minutes before requesting another code.";
  }
  if (
    msg === PhoneVerificationError.SMS_COOLDOWN ||
    msg === PhoneVerificationError.SMS_DAILY_LIMIT
  ) {
    const seconds =
      retryAfterSeconds !== undefined && Number.isFinite(retryAfterSeconds)
        ? Math.max(0, Math.ceil(retryAfterSeconds))
        : undefined;
    const wait =
      seconds === undefined
        ? msg === PhoneVerificationError.SMS_DAILY_LIMIT
          ? "up to 24 hours"
          : "60 seconds"
        : seconds < 60
          ? `${seconds} second${seconds === 1 ? "" : "s"}`
          : seconds < 3600
            ? `${Math.ceil(seconds / 60)} minute${seconds <= 60 ? "" : "s"}`
            : `${Math.ceil(seconds / 3600)} hour${seconds <= 3600 ? "" : "s"}`;
    return msg === PhoneVerificationError.SMS_DAILY_LIMIT
      ? `You've reached the daily SMS verification limit. Try again in ${wait}, or verify through Messages now. If you can't use Messages, contact support@terac.com.`
      : `Please wait ${wait} before requesting another SMS code, even if you change numbers. You can still enter a code you've already received or verify through Messages.`;
  }
  if (msg === PhoneVerificationError.ADDITIONAL_VERIFICATION_REQUIRED) {
    return "For account security, verify your phone through Messages before requesting more SMS codes. If you can't use Messages, contact support@terac.com.";
  }
  if (msg === PhoneVerificationError.SMS_UNAVAILABLE) {
    return "SMS verification is temporarily unavailable. Verify through Messages, or contact support@terac.com if you can't use Messages.";
  }
  if (msg === PhoneVerificationError.VOIP_NOT_SUPPORTED) {
    return "VOIP numbers are not supported. Please use a mobile number.";
  }
  if (msg === PhoneVerificationError.SMS_GEO_BLOCKED) {
    return "We don\u2019t currently support phone verification for this country. Please use a phone number from a supported country.";
  }
  if (msg === PhoneVerificationError.DESTINATION_BLOCKED) {
    return "We can\u2019t send verification codes to this number right now. Please try a different number or contact support.";
  }
  if (msg === PhoneVerificationError.NUMBER_UNREACHABLE) {
    return "We can\u2019t reach this number by text. Please try a different number, or contact support@terac.com if it is the only one you have.";
  }
  if (msg === PhoneVerificationError.LANDLINE_NOT_SUPPORTED) {
    return "This looks like a landline, which can\u2019t receive text messages. Please use a mobile number.";
  }
  if (msg === PhoneVerificationError.BANNED_ACCOUNT) {
    return "This phone number is associated with an account that was banned. Please contact support@terac.com if you believe this is an error.";
  }
  if (msg === PhoneVerificationError.COUNTRY_NOT_SUPPORTED) {
    const countryName = getCountryDisplayName(country ?? null);
    return countryName
      ? `We don\u2019t currently support participants from ${countryName}. Check back soon!`
      : "We don\u2019t currently support participants from your country. Check back soon!";
  }
  return "We couldn\u2019t send your verification code right now. Please try again in a few minutes.";
}

// Countries where Twilio SMS verification is disabled via Geo-Permissions.
// Maintained manually; update when Twilio console settings change.
// Uses ISO 3166-1 alpha-2 country codes.
export const TWILIO_SMS_BLOCKED_COUNTRIES = new Set([
  // Asia
  "AF", // Afghanistan
  "AZ", // Azerbaijan
  "BH", // Bahrain
  "BT", // Bhutan
  "IO", // British Indian Ocean Territory
  "BN", // Brunei
  "KH", // Cambodia
  "CX", // Christmas Island
  "CC", // Cocos (Keeling) Islands
  "CY", // Cyprus
  "IQ", // Iraq
  "IL", // Israel
  "JO", // Jordan
  "KZ", // Kazakhstan
  "KW", // Kuwait
  "KG", // Kyrgyzstan
  "LA", // Laos
  "LB", // Lebanon
  "MO", // Macau
  "MV", // Maldives
  "MN", // Mongolia
  "MM", // Myanmar
  "OM", // Oman
  "PS", // Palestinian Territory
  "QA", // Qatar
  "LK", // Sri Lanka
  "TW", // Taiwan
  "TJ", // Tajikistan
  "TL", // Timor-Leste
  "TM", // Turkmenistan
  "UZ", // Uzbekistan
  "VN", // Vietnam
  "YE", // Yemen
  // Africa
  "DZ", // Algeria
  "AO", // Angola
  "BJ", // Benin
  "BW", // Botswana
  "BF", // Burkina Faso
  "BI", // Burundi
  "CM", // Cameroon
  "CV", // Cape Verde
  "CF", // Central African Republic
  "TD", // Chad
  "KM", // Comoros
  "CG", // Congo
  "CI", // Cote d'Ivoire
  "CD", // DR Congo
  "DJ", // Djibouti
  "GQ", // Equatorial Guinea
  "ER", // Eritrea
  "ET", // Ethiopia
  "GA", // Gabon
  "GM", // Gambia
  "GH", // Ghana
  "GN", // Guinea
  "GW", // Guinea-Bissau
  "KE", // Kenya
  "LS", // Lesotho
  "LR", // Liberia
  "LY", // Libya
  "MG", // Madagascar
  "MW", // Malawi
  "ML", // Mali
  "MR", // Mauritania
  "MU", // Mauritius
  "YT", // Mayotte
  "MA", // Morocco
  "MZ", // Mozambique
  "NA", // Namibia
  "NE", // Niger
  "RE", // Reunion
  "SH", // Saint Helena
  "ST", // Sao Tome and Principe
  "SN", // Senegal
  "SC", // Seychelles
  "SL", // Sierra Leone
  "SO", // Somalia
  "SS", // South Sudan
  "SD", // Sudan
  "SZ", // Eswatini (Swaziland)
  "TZ", // Tanzania
  "TG", // Togo
  "TN", // Tunisia
  "UG", // Uganda
  "EH", // Western Sahara
  "ZM", // Zambia
  "ZW", // Zimbabwe
]);

// Countries Linq cannot deliver SMS to (per Linq's "SMS Not Available" list).
// Gates the Linq inbound-token verification path the same way
// TWILIO_SMS_BLOCKED_COUNTRIES gates the Twilio send path. This list is the
// SMS-only subset; Linq's separate MMS gaps affect media outreach, not the
// text-only verification token, so they are intentionally omitted here.
// Uses ISO 3166-1 alpha-2 country codes. Maintained manually from Linq's list.
//
// Most of these are countries Terac otherwise operates in (e.g. CI, CK, CW, CX,
// FO, NC, VU, WS, YT), so this gate is what actually stops the Linq token for
// them. The four that are not (AX, AC, BQ, PS) are already caught earlier by the
// platform-access gate, so listing them here is belt-and-suspenders.
//
// Not gateable by ISO country code (share a calling code with a supported
// country, or have no distinct alpha-2): Azores/Madeira (+351 PT),
// Canary Islands (+34 ES), Easter Island (+56 CL), Alderney/Sark (GG).
export const LINQ_SMS_BLOCKED_COUNTRIES = new Set([
  "AX", // Aland Islands
  "AC", // Ascension Island
  "BL", // Saint Barthelemy
  "BQ", // Bonaire, Sint Eustatius and Saba
  "CI", // Cote d'Ivoire
  "CK", // Cook Islands
  "CW", // Curacao
  "CX", // Christmas Island
  "EH", // Western Sahara
  "FM", // Federated States of Micronesia
  "FO", // Faroe Islands
  "GF", // French Guiana
  "GQ", // Equatorial Guinea
  "IM", // Isle of Man
  "MF", // Saint Martin
  "NC", // New Caledonia
  "NR", // Nauru
  "PS", // Palestinian Territories
  "SJ", // Svalbard and Jan Mayen
  "SX", // Sint Maarten
  "VA", // Vatican City
  "VU", // Vanuatu
  "WS", // Samoa
  "YT", // Mayotte
]);
